/**
 * 🟥 SaveToGitHubButton — atajo dentro del Workspace (Modo Alternativo).
 *
 * Re-implementado en términos del nuevo SaveToGitHubModal compartido.
 * Mantenemos este componente para que el botón siga disponible JUNTO a
 * "Descargar ZIP" en el Modo Alternativo, pero ya no duplica la lógica del
 * modal: solo abre el modal nuevo (igual que el botón del header).
 */
import { useState, useEffect, useCallback } from 'react';
import { Github } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SaveToGitHubModal from './SaveToGitHubModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchJSON = async (path) => {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const SaveToGitHubButton = ({ files = [], defaultRepoName = 'damyxia-project' }) => {
  const { t, language } = useLanguage();
  const [eligible, setEligible] = useState(false);
  const [connected, setConnected] = useState(false);
  const [scopeOk, setScopeOk] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const elig = await fetchJSON('/api/github/eligibility');
      setEligible(!!elig.eligible);
      if (!elig.eligible) return;
      try {
        const status = await fetchJSON('/api/github/status');
        setConnected(!!status.connected);
        setScopeOk(!!status.scope_ok);
      } catch {
        setConnected(false);
        setScopeOk(false);
      }
    } catch {
      setEligible(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!eligible) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium border transition-colors hover:bg-zinc-800"
        style={{ borderColor: '#374151', backgroundColor: '#111827' }}
        data-testid="save-to-github-btn"
        title={t('github.saveToGitHub')}
      >
        <Github className="w-4 h-4" />
        {t('github.saveToGitHub')}
      </button>

      <SaveToGitHubModal
        open={open}
        onClose={() => setOpen(false)}
        files={files}
        connected={connected}
        scopeOk={scopeOk}
        onRefreshStatus={refresh}
        language={language}
        defaultRepoName={defaultRepoName}
      />
    </>
  );
};

export default SaveToGitHubButton;
